<template>
  <div
    class="ai-modal tw:relative tw:flex tw:flex-col tw:rounded-[24px] tw:bg-white tw:p-px"
    :class="{
      minimise: aiModalMinimise,
      left: aiModalLeft && !isMobile,
      mobile: isMobile,
    }"
  >
    <Transition
      mode="out-in"
      name="panel"
    >
      <Component
        :is="aiModalPanelComponent"
        class="tw:absolute tw:bottom-px tw:left-px tw:right-px tw:top-px"
        @close="close"
        @home="home"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, inject, markRaw, onBeforeMount, watch } from "vue";

import * as aiPanels from "shared/components/AI/AIPanels";
import useAIStore from "shared/stores/ai";
import useUserStore from "shared/stores/user";

import ModalService from "./ModalService";

type AIPanelType = keyof typeof aiPanels;
type AIPanelComponent = (typeof aiPanels)[AIPanelType];

const emit = defineEmits(["close"]);

const aiStore = useAIStore();

const { aiModalLeft, aiModalMinimise, aiModalOpen, aiModalPanel } =
  storeToRefs(aiStore);

const isMobile = inject<boolean>("isMobile");

const aiModalPanelComponent = computed<AIPanelComponent>(() =>
  markRaw(aiPanels[aiModalPanel.value])
);

function home() {
  aiModalPanel.value = "AIAssistantPanel";
}

function close() {
  aiModalOpen.value = false;
  ModalService.close("AIModal");
  emit("close");
}

onBeforeMount(() => {
  aiModalOpen.value = true;
});

const { isLoggedIn } = storeToRefs(useUserStore());

watch(isLoggedIn, close);
</script>

<style scoped lang="scss">
.panel-leave-active,
.panel-enter-active {
  transition: 0.3s;
}

.panel-enter-from {
  opacity: 0;
}

.panel-leave-to {
  opacity: 0;
}

.ai-modal {
  bottom: 20px;
  box-shadow:
    0 0 10px 10px #0000000a,
    0 20px 25px 0 #0000001a;
  height: calc(100vh - 100px);
  position: fixed;
  right: 20px;
  transition: all 0.3s ease;
  width: 580px;
  z-index: 1;

  &.minimise {
    height: 320px;
  }

  &.left {
    right: calc(100vw - 600px);
  }

  &.mobile {
    inset: calc(env(safe-area-inset-top) + 10px)
      calc(env(safe-area-inset-right, 0px) + 10px)
      calc(env(safe-area-inset-bottom, 0px) + 10px)
      calc(env(safe-area-inset-left) + 10px);
    height: auto;
    width: auto;
  }
}
</style>
